import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from "react";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useDispatch } from "react-redux";
import { useGetQuickPickData } from "../../../hooks/data/useGetQuickPickData";
import { userLogoutAction } from "../../../redux/actions/loginImport";
import { useNavigate } from "react-router-dom";
import { DateTime } from 'luxon';
import { useLocation } from "react-router-dom";

const QuickPickGamesContext = createContext();

export const useQuickPickGamesContext = () => {
    const context = useContext(QuickPickGamesContext);
    if (!context) {
        throw new Error("useQuickPickGamesContext must be used within a QuickPickGamesProvider");
    }
    return context;
}

export const QuickPickGamesProvider = ({ children }) => {
    const {
        gameData,
        masterPoolData,
        pickState,
        hasJoinDeadlinePassed,
        leaderboardData,
        setInterval,
        gameAddress,
        page,
        masterPoolId,
        gameId,
        pickIndex,
        interval,
    } = useUserAndGameContext();

    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const intervalParam = searchParams.get('interval');
    const gameDateParam = searchParams.get('gameDate');

    // Prevent navigation on initial render
    const currentPage = useRef(page);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    }

    const timeNowInNewYork = DateTime.now().setZone('America/New_York');

    const redisKeys = masterPoolData?.redisKeys;
    const filterByDate = masterPoolData?.gameParameters?.filterByDate;

    const [hasDataFetched, setHasDataFetched] = useState(false);
    const [hasTheCurrentIntervalStarted, setHasTheCurrentIntervalStarted] = useState(null);
    const [hasTheLastGameOfCurrentIntervalStarted, setHasTheLastGameOfCurrentIntervalStarted] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [initialPageLoad, setInitialPageLoad] = useState(true);

    // Get data
    const { 
        mutate: getQuickPickData, 
        data: quickPickData, 
        isLoading: quickPickDataLoading, 
        isError: quickPickDataError, 
        error: quickPickDataErrorData 
    } = useGetQuickPickData();

    // Fetch data on mount
    useEffect(() => {
        if (!hasDataFetched && redisKeys) {
            getQuickPickData({ redisKeys });
            setHasDataFetched(true);
        }
    }, [hasDataFetched, redisKeys, getQuickPickData]);

    // Error handling
    useEffect(() => {
        if (quickPickDataError) {
            console.error("Error fetching quick pick data:", quickPickDataErrorData);
            if (quickPickDataErrorData?.response?.status === 401) {
                dispatch(userLogoutAction({ logoutType: 'token-expired' }));
                navigate('/login');
            } else {
                navigate('/404/pool-hall');
            }
        }
    }, [quickPickDataError, quickPickDataErrorData, dispatch, navigate]);

    const { fullSchedule, createdSchedule, teams, quickPickGames, currentInterval, standings } = quickPickData || {};

    // Core data calculations
    const gamesToInclude = useMemo(() => {
        return gameData?.gameParameters?.gamesToInclude || masterPoolData?.gameParameters?.gamesToInclude || [];
    }, [gameData?.gameParameters?.gamesToInclude, masterPoolData?.gameParameters?.gamesToInclude]);

    const schedule = useMemo(() => {
        if (!gamesToInclude || (!fullSchedule && !createdSchedule)) return [];
        
        // Start with the games from fullSchedule that match gamesToInclude
        const filteredMainSchedule = fullSchedule 
            ? fullSchedule.filter(game => gamesToInclude.includes(game.GlobalGameID)) 
            : [];
        
        // If createdSchedule exists, add games from it that match gamesToInclude
        const filteredCreatedSchedule = createdSchedule 
            ? createdSchedule.filter(game => gamesToInclude.includes(game.GlobalGameID)) 
            : [];
        
        // Combine both schedules (no need to deduplicate as there are no duplicates)
        return [...filteredMainSchedule, ...filteredCreatedSchedule];
        
    }, [fullSchedule, createdSchedule, gamesToInclude]);

    const firstInterval = useMemo(() => {
        return gameData?.gameParameters?.firstInterval || masterPoolData?.gameParameters?.firstInterval || 0;
    }, [gameData?.gameParameters?.firstInterval, masterPoolData?.gameParameters?.firstInterval]);

    const lastInterval = useMemo(() => {
        return gameData?.gameParameters?.lastInterval || masterPoolData?.gameParameters?.lastInterval || 0;
    }, [gameData?.gameParameters?.lastInterval, masterPoolData?.gameParameters?.lastInterval]);

    const propIdsToUse = useMemo(() => {
        return gameData?.gameParameters?.propIdsToUse || masterPoolData?.gameParameters?.propIdsToUse || [];
    }, [gameData?.gameParameters?.propIdsToUse, masterPoolData?.gameParameters?.propIdsToUse]);

    const theCurrentInterval = useMemo(() => {
        if (!currentInterval) return {};
        if (currentInterval >= firstInterval && currentInterval <= lastInterval) {
            return currentInterval;
        } else if (currentInterval < firstInterval) {
            return firstInterval;
        } else if (currentInterval > lastInterval) {
            return lastInterval;
        } 
    }, [currentInterval, firstInterval, lastInterval]);

    const oneWeekEvent = useMemo(() => {
        return firstInterval === lastInterval;
    }, [firstInterval, lastInterval]);

    // Date/interval mapping
    const dateToIntervalMap = useMemo(() => {
        if (!schedule || !propIdsToUse) return {};
        
        const mapping = {};
        
        schedule.forEach(game => {
            if (!game.Day) return;
            
            for (const prop of propIdsToUse) {
                if (prop.gameId === game.GlobalGameID) {
                    mapping[game.Day] = prop.interval;
                    break;
                }
            }
        });
        
        return mapping;
    }, [schedule, propIdsToUse]);

    const intervalToDatesMap = useMemo(() => {
        const mapping = {};
        
        for (let i = firstInterval; i <= lastInterval; i++) {
            mapping[i] = [];
        }
        
        Object.entries(dateToIntervalMap).forEach(([date, intervalValue]) => {
            if (mapping[intervalValue]) {
                mapping[intervalValue].push(date);
            }
        });
        
        Object.keys(mapping).forEach(intervalKey => {
            mapping[intervalKey].sort((a, b) => 
                DateTime.fromISO(a).valueOf() - DateTime.fromISO(b).valueOf()
            );
        });
        
        return mapping;
    }, [dateToIntervalMap, firstInterval, lastInterval]);

    // Extract unique days
    const uniqueDays = useMemo(() => {
        if (!schedule || !schedule.length) return [];

        const uniqueDaysSet = new Set();
        schedule.forEach(game => {
            if (game.Day) {
                uniqueDaysSet.add(game.Day);
            }
        });

        return Array.from(uniqueDaysSet).sort((a, b) => 
            DateTime.fromISO(a).valueOf() - DateTime.fromISO(b).valueOf()
        );
    }, [schedule]);

    // Find default date
    const defaultDate = useMemo(() => {
        if (!uniqueDays.length) return null;
        
        const today = DateTime.now().setZone('America/New_York').toISODate();
        const yesterday = DateTime.now().setZone('America/New_York').minus({ days: 1 }).toISODate();
        const currentDate = timeNowInNewYork.hour < 4 ? yesterday : today;
        
        // Find today's date
        const todayMatch = uniqueDays.find(date => date.startsWith(currentDate));
        if (todayMatch) return todayMatch;
        
        // Find future date
        const futureDate = uniqueDays.find(date => date > currentDate);
        if (futureDate) return futureDate;
        
        // Use most recent date
        return uniqueDays[uniqueDays.length - 1];
    }, [uniqueDays]);

    // Get the default date gameId(s)
    const gameIdsForDefaultDate = useMemo(() => {
        if (!schedule || !defaultDate) return [];

        return schedule?.filter(game => game.Day === defaultDate)
            .map(game => game.GlobalGameID);
    }, [schedule, defaultDate]);

    // Get the default date propIds count
    const picksForDefaultDate = useMemo(() => {
        if (!propIdsToUse || !gameIdsForDefaultDate) return 0;

        return propIdsToUse.filter(prop =>
            gameIdsForDefaultDate.includes(prop.gameId) &&
            Number(prop.interval) === Number(theCurrentInterval)
        ).reduce((acc, prop) => {
            const propsCount = prop.totalProps !== undefined ? prop.totalProps : prop.propIdsToUse.length;
            return acc + propsCount;
        }, 0);
    }, [propIdsToUse, gameIdsForDefaultDate, theCurrentInterval]);

    // Check if date is valid (in uniqueDays)
    function isDateValid(date) {
        if (!date || !uniqueDays) return false;
        return uniqueDays.includes(date);
    }

 // Single useEffect to handle URL parameter validation and synchronization
useEffect(() => {
    // If page is NOT my-picks, leaderboard, or pool-picks, remove URL params and set defaults
    if (page !== 'my-picks' && page !== 'leaderboard' && page !== 'pool-picks') {
        if (intervalParam || gameDateParam) {
            const newURL = new URL(window.location.href);
            newURL.searchParams.delete('interval');
            newURL.searchParams.delete('gameDate');
            window.history.replaceState({}, '', newURL);
        }
        setSelectedDate(filterByDate ? defaultDate : null);
        setInterval(theCurrentInterval);
        return; // Exit early for non-game pages
    }
    
    // For game pages, check if parameters are valid and consistent
    if (filterByDate && defaultDate && theCurrentInterval !== undefined) {
        let needsReset = false;
        
        // Check if date is valid
        if (gameDateParam && !uniqueDays.includes(gameDateParam)) {
            needsReset = true;
        }
        
        // Check if interval is valid
        const intervalValue = intervalParam ? parseInt(intervalParam, 10) : null;
        if (intervalValue && (intervalValue < firstInterval || intervalValue > lastInterval)) {
            needsReset = true;
        }
        
        // Check if date and interval match
        if (!needsReset && gameDateParam && intervalValue) {
            const intervalFromDate = dateToIntervalMap[gameDateParam];
            if (intervalFromDate && Number(intervalFromDate) !== intervalValue) {
                needsReset = true;
            }
        }
        
        // If any validation failed, reset to defaults
        if (needsReset) {
            
            // Update URL
            const newURL = new URL(window.location.href);
            newURL.searchParams.set('interval', theCurrentInterval);
            newURL.searchParams.set('gameDate', defaultDate);
            window.history.replaceState({}, '', newURL);
            
            // Update state
            setInterval(theCurrentInterval);
            setSelectedDate(defaultDate);
        } else {
            // Parameters are valid, use them
            setInterval(intervalValue || theCurrentInterval);
            setSelectedDate(gameDateParam || defaultDate);
        }
    } else if (!filterByDate) {
        // In non-date filtering mode, just handle interval
        const intervalValue = intervalParam ? parseInt(intervalParam, 10) : null;
        let needsReset = false;
        
        // Check if interval is valid
        if (intervalValue && (intervalValue < firstInterval || intervalValue > lastInterval)) {
            needsReset = true;
        }
        
        // Remove any gameDate param
        if (gameDateParam) {
            const newURL = new URL(window.location.href);
            newURL.searchParams.delete('gameDate');
            window.history.replaceState({}, '', newURL);
        }
        
        if (needsReset) {
            // Reset to default interval
            const newURL = new URL(window.location.href);
            newURL.searchParams.set('interval', theCurrentInterval);
            window.history.replaceState({}, '', newURL);
            
            setInterval(theCurrentInterval);
        } else {
            // Use valid interval
            setInterval(intervalValue || theCurrentInterval);
        }
        
        // Always set selectedDate to null in non-filterByDate mode
        setSelectedDate(null);
    }
    
}, [
    page, 
    intervalParam, 
    gameDateParam, 
    filterByDate, 
    defaultDate, 
    theCurrentInterval, 
    firstInterval, 
    lastInterval, 
    uniqueDays,
    dateToIntervalMap,
    setInterval,
]);
    // Get game IDs for filtering
    const gameIdsForInterval = useMemo(() => {
        if (!propIdsToUse || !interval) return [];
        
        return propIdsToUse
            .filter(item => Number(item.interval) === Number(interval))
            .map(item => item.gameId);
    }, [propIdsToUse, interval]);

    // Apply primary filtering by interval
    const filteredByIntervalSchedule = useMemo(() => {
        if (!schedule || !gameIdsForInterval.length) return [];
        
        return schedule
            .filter(game => gameIdsForInterval.includes(game.GlobalGameID))
            .sort((a, b) => DateTime.fromISO(a.DateTime).valueOf() - DateTime.fromISO(b.DateTime).valueOf());
    }, [schedule, gameIdsForInterval]);

    const filteredByIntervalQuickPickGames = useMemo(() => {
        if (!quickPickGames || !gameIdsForInterval.length) return [];
        
        return quickPickGames
            .filter(game => gameIdsForInterval.includes(game.GlobalGameID))
            .sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
    }, [quickPickGames, gameIdsForInterval]);

    // Get unique days for the current interval
    const uniqueDaysForCurrentInterval = useMemo(() => {
        if (!filteredByIntervalSchedule || !filteredByIntervalSchedule.length) return [];

        const uniqueDaysSet = new Set();
        filteredByIntervalSchedule.forEach(game => {
            if (game.Day) {
                uniqueDaysSet.add(game.Day);
            }
        });

        return Array.from(uniqueDaysSet).sort((a, b) => 
            DateTime.fromISO(a).valueOf() - DateTime.fromISO(b).valueOf()
        );
    }, [filteredByIntervalSchedule]);

    // Apply secondary filtering by date if needed
    const activeSchedule = useMemo(() => {
        if (!filterByDate || !selectedDate || !filteredByIntervalSchedule) {
            //console.log("No date filtering applied, returning full schedule");
            return filteredByIntervalSchedule;
        }
        
        return filteredByIntervalSchedule.filter(game => game.Day === selectedDate);
    }, [filterByDate, selectedDate, filteredByIntervalSchedule]);

    const activeQuickPickGames = useMemo(() => {
        if (!filterByDate || !selectedDate || !filteredByIntervalQuickPickGames) {
            return filteredByIntervalQuickPickGames;
        }
        
        return filteredByIntervalQuickPickGames.filter(game => game.Day === selectedDate);
    }, [filterByDate, selectedDate, filteredByIntervalQuickPickGames]);

    // Get filtered propIds for the selected interval
    const filteredPropIds = useMemo(() => {
        if (!propIdsToUse || !interval) return [];
        
        const intervalProps = propIdsToUse.filter(item => 
            Number(item.interval) === Number(interval)
        );
        
        return intervalProps.flatMap(item => item.propIdsToUse);
    }, [propIdsToUse, interval]);

    // Get game IDs for the current active interval
    const gameIdsForCurrentInterval = useMemo(() => {
        if (!propIdsToUse || !theCurrentInterval) return [];
        
        return propIdsToUse
            .filter(item => Number(item.interval) === Number(theCurrentInterval))
            .map(item => item.gameId);
    }, [propIdsToUse, theCurrentInterval]);

    // Get filtered propIds for the current active interval
    const filteredPropIdsForTheCurrentInterval = useMemo(() => {
        if (!propIdsToUse || !theCurrentInterval) return [];
        
        return propIdsToUse.filter(item => 
            Number(item.interval) === Number(theCurrentInterval)
        );
    }, [propIdsToUse, theCurrentInterval]);

    // Calculate total picks for the current active interval
    const picksForTheCurrentInterval = useMemo(() => {
        const totalPicks = filteredPropIdsForTheCurrentInterval.reduce((acc, game) => {
            const propsCount = game.totalProps !== undefined ? game.totalProps : game.propIdsToUse.length;
            return acc + propsCount;
        }, 0);
        
        return totalPicks;
    }, [filteredPropIdsForTheCurrentInterval]);


    const picksForTheSelectedDate = useMemo(() => {
        // Return null if not using date filtering
        if (!filterByDate || !selectedDate) return null;
        
        // Filter propIds that match both interval and selected date
        if (!activeSchedule || !propIdsToUse) return 0;
        
        // Get all game IDs from the active schedule (which is already filtered by date)
        const activeGameIds = activeSchedule.map(game => game.GlobalGameID);

        // Filter props that belong to both current interval and selected date
        const dateFilteredProps = propIdsToUse.filter(prop => 
            Number(prop.interval) === Number(interval) && 
            activeGameIds.includes(prop.gameId)
        );
        
        // Calculate total props for these games
        const totalPicks = dateFilteredProps.reduce((acc, game) => {
            const propsCount = game.totalProps !== undefined ? game.totalProps : game.propIdsToUse.length;
            return acc + propsCount;
        }, 0);
        
        return totalPicks;
    }, [filterByDate, selectedDate, activeSchedule, propIdsToUse, interval]);

    const activeGameIds = useMemo(() => {
        if (!activeSchedule || !activeSchedule.length) return [];

        return activeSchedule.map(game => game.GlobalGameID);
    }, [activeSchedule]);

    const propsForActiveGameIds = useMemo(() => {
        if (!propIdsToUse || !activeGameIds) return [];

        return propIdsToUse.filter(prop =>
            activeGameIds.includes(prop.gameId) &&
            Number(prop.interval) === Number(interval)
        );
    }, [propIdsToUse, activeGameIds, interval]);


    // Calculate total picks across all intervals
    const picksForAllIntervals = useMemo(() => {
        const totalPicks = propIdsToUse.reduce((acc, game) => {
            const propsCount = game.totalProps !== undefined ? game.totalProps : game.propIdsToUse.length;
            return acc + propsCount;
        }, 0);
        
        return totalPicks;
    }, [propIdsToUse]);

    // Calculate picks for the selected interval
    const picksThisInterval = useMemo(() => {
        const intervalProps = propIdsToUse.filter(item => 
            Number(item.interval) === Number(interval)
        );

        const totalPicks = intervalProps.reduce((acc, game) => {
            const propsCount = game.totalProps !== undefined ? game.totalProps : game.propIdsToUse.length;
            return acc + propsCount;
        }, 0);

        return totalPicks;
    }, [propIdsToUse, interval]);

    // Determine if interval has started/ended
    useEffect(() => {
        if (!activeSchedule || !activeSchedule.length) {
            setHasTheCurrentIntervalStarted(false);
            setHasTheLastGameOfCurrentIntervalStarted(false);
            return;
        }

        const currentIntervalStart = activeSchedule[0]?.DateTime;
        const currentIntervalEnd = activeSchedule[activeSchedule.length - 1]?.DateTime;

        if (currentIntervalStart && currentIntervalEnd) {
            const currentIntervalStartObj = DateTime.fromISO(currentIntervalStart, { zone: 'America/New_York' });
            const currentIntervalEndObj = DateTime.fromISO(currentIntervalEnd, { zone: 'America/New_York' });

            setHasTheCurrentIntervalStarted(timeNowInNewYork > currentIntervalStartObj);
            setHasTheLastGameOfCurrentIntervalStarted(timeNowInNewYork > currentIntervalEndObj);
        } else {
            setHasTheCurrentIntervalStarted(false);
            setHasTheLastGameOfCurrentIntervalStarted(false);
        }
    }, [activeSchedule, timeNowInNewYork]);

    const usingTiebreaker = useMemo(() => {
        return gameData?.gameParameters?.usingTiebreaker || masterPoolData?.gameParameters?.usingTiebreaker || false;
    }, [gameData?.gameParameters?.usingTiebreaker, masterPoolData?.gameParameters?.usingTiebreaker]);

    // Format day options for display
    const dayOptions = useMemo(() => {
        if (!uniqueDaysForCurrentInterval) return [];
        
        return uniqueDaysForCurrentInterval.map(day => ({
            value: day,
            label: DateTime.fromISO(day).toFormat('EEE, MMM d, yyyy')
        }));
    }, [uniqueDaysForCurrentInterval]);

    const hideLine = useMemo(() => {
        return masterPoolData?.gameParameters?.hideLine || false;
    }, [masterPoolData?.gameParameters?.hideLine]);
    
    const isUserPaid = useMemo(() => {
        return false; // Placeholder, replace with actual logic if needed
    }, []);

    const usingWeeks = useMemo(() => {
        return masterPoolData?.intervalInfo?.type === 'weeks';
    }, [masterPoolData?.intervalInfo?.type]);
    //console.log("Using Weeks:", usingWeeks);

    const QuickPickGamesContextValues = useMemo(() => ({
        // Core data
        schedule,
        teams,
        standings,
        quickPickGames,
        theCurrentInterval,
        firstInterval,
        lastInterval,
        propIdsToUse,
        oneWeekEvent,
        hideLine,
        usingWeeks,
        
        // Filtered data
        filteredByIntervalSchedule,
        filteredByIntervalQuickPickGames,
        activeSchedule,
        activeQuickPickGames,
        activeGameIds,
        
        // Date filtering
        filterByDate,
        uniqueDays,
        uniqueDaysForCurrentInterval,
        selectedDate,
        setSelectedDate,
        dateToIntervalMap,
        intervalToDatesMap,
        dayOptions,
        picksForTheSelectedDate,
        defaultDate,
        gameIdsForDefaultDate,
        picksForDefaultDate,
        propsForActiveGameIds,
        
        // Game and prop IDs
        gameIdsForInterval,
        filteredPropIds,
        
        // Game status
        usingTiebreaker,
        picksThisInterval,
        picksForTheCurrentInterval,
        picksForAllIntervals,
        hasTheCurrentIntervalStarted,
        hasTheLastGameOfCurrentIntervalStarted,
        isUserPaid,
    }), [
        schedule, teams, standings, quickPickGames, 
        theCurrentInterval, firstInterval, lastInterval, propIdsToUse, oneWeekEvent,
        filteredByIntervalSchedule, filteredByIntervalQuickPickGames, 
        activeSchedule, activeQuickPickGames,
        filterByDate, uniqueDays, uniqueDaysForCurrentInterval, selectedDate,
        dateToIntervalMap, intervalToDatesMap, dayOptions,
        gameIdsForInterval, filteredPropIds,
        usingTiebreaker, picksThisInterval, picksForTheCurrentInterval, picksForAllIntervals,
        hasTheCurrentIntervalStarted, hasTheLastGameOfCurrentIntervalStarted, defaultDate,
        gameIdsForDefaultDate, picksForDefaultDate, activeGameIds, picksForTheSelectedDate,
        propsForActiveGameIds, hideLine, isUserPaid, usingWeeks
    ]);

    // Only show loading screen if we're still loading core data
    if (!schedule || !teams || !quickPickGames) {
        return <LoadingScreen />;
    };

    return (
        <QuickPickGamesContext.Provider value={QuickPickGamesContextValues}>
            {children}
        </QuickPickGamesContext.Provider>
    );
}

export default QuickPickGamesContext;